import React from "react"

import { CodeBlock } from "@javascript/components/ui/code-block"
import {
  TypographyH2,
  TypographyH3,
  TypographyInlineCode,
  TypographyList,
  TypographyP
} from "@javascript/components/ui/typography"

interface StylingAndAssetsPageProps {
  assetReferenceExample: string
}

export function StylingAndAssetsPage({ assetReferenceExample }: StylingAndAssetsPageProps) {
  return (
    <div className="space-y-8">
      <div>
        <TypographyH2>Styling & Assets Reference</TypographyH2>
        <TypographyP>
          Themes can include CSS, images, and an optional declared JavaScript runtime. Learn how each asset type enters
          the rendered invitation.
        </TypographyP>
      </div>

      <div>
        <TypographyH3>Referencing theme assets</TypographyH3>
        <TypographyP>
          Always reference assets through the theme's asset helper, never with hardcoded filesystem paths. This allows the platform to serve assets from flexible storage locations (disk, CDN, cloud storage) without requiring your template to know or change.
        </TypographyP>
        <TypographyP>
          Use the <TypographyInlineCode>{`{% asset_url 'filename' %}`}</TypographyInlineCode> tag:
        </TypographyP>
        <CodeBlock code={assetReferenceExample} language="liquid" className="my-4" />
        <TypographyP className="text-sm text-muted-foreground">
          The stylesheet <TypographyInlineCode>&lt;link&gt;</TypographyInlineCode> tag shows the correct pattern: <TypographyInlineCode>{`href="{% asset_url 'style.css' %}"`}</TypographyInlineCode>
        </TypographyP>
      </div>

      <div>
        <TypographyH3>CSS restrictions for theme safety</TypographyH3>
        <TypographyP>
          To keep themes isolated and security-compliant, a theme's CSS must follow these restrictions:
        </TypographyP>
        <TypographyList>
          <li><TypographyInlineCode>@import</TypographyInlineCode> is forbidden — include all CSS inline or via separate <TypographyInlineCode>&lt;link&gt;</TypographyInlineCode> tags</li>
          <li>Off-origin <TypographyInlineCode>url()</TypographyInlineCode> references are forbidden — use <TypographyInlineCode>{`{% asset_url %}`}</TypographyInlineCode> for same-origin assets only</li>
          <li><TypographyInlineCode>expression()</TypographyInlineCode> (IE-era syntax) is forbidden</li>
        </TypographyList>
        <TypographyP className="mt-4 text-sm text-muted-foreground">
          These are the CSS rules a theme package must satisfy under the platform's design contract (see
          theme-architecture.md §7). The loader does not yet automate every CSS check, so curated review is the current
          enforcement boundary.
        </TypographyP>
      </div>

      <div>
        <TypographyH3>JavaScript entrypoints</TypographyH3>
        <TypographyP>
          A contract v3 package may include <TypographyInlineCode>assets/theme.js</TypographyInlineCode>, but it is not
          referenced with <TypographyInlineCode>asset_url</TypographyInlineCode> or a script tag. Declare that path as
          <TypographyInlineCode>runtime.entrypoint</TypographyInlineCode> in <TypographyInlineCode>theme.json</TypographyInlineCode>.
          The platform serves only that declared JavaScript file and loads it after any approved dependencies.
        </TypographyP>
        <TypographyP className="mt-4 text-sm text-muted-foreground">
          Runtime files receive the same curated review as application code. Remote scripts, CDN URLs, and undeclared
          JavaScript assets are not supported.
        </TypographyP>
      </div>

      <div>
        <TypographyH3>File structure</TypographyH3>
        <TypographyP>
          Place your stylesheet in an <TypographyInlineCode>assets/</TypographyInlineCode> folder under your theme:
        </TypographyP>
        <TypographyList>
          <li><TypographyInlineCode>themes/your-theme-key/assets/style.css</TypographyInlineCode> — required; every theme package must include it</li>
          <li><TypographyInlineCode>themes/your-theme-key/assets/theme.js</TypographyInlineCode> — optional; must be the contract v3 runtime entrypoint</li>
          <li><TypographyInlineCode>themes/your-theme-key/preview.png</TypographyInlineCode> — required; catalog preview image, at the theme root (not under <TypographyInlineCode>assets/</TypographyInlineCode>)</li>
        </TypographyList>
        <TypographyP className="mt-4 text-sm text-muted-foreground">
          Additional images referenced by your CSS or template can also live under <TypographyInlineCode>assets/</TypographyInlineCode> (flat, alongside <TypographyInlineCode>style.css</TypographyInlineCode>) — reference them the same way, through <TypographyInlineCode>{`{% asset_url 'filename' %}`}</TypographyInlineCode>, never a hardcoded path.
        </TypographyP>
      </div>
    </div>
  )
}
